import Phaser from "phaser";

/**
 * Character sprite sheet handling — uses LimeZu Modern Interiors free chars.
 *
 * Each character sheet (after 2x scaling) is 768 × 64, with 24 frames of
 * 32×64 each laid out in a single row. The sheet contains 4 directions ×
 * 6 frames per direction:
 *
 *   frames  0..5  : right (east)
 *   frames  6..11 : up    (north)
 *   frames 12..17 : left  (west)
 *   frames 18..23 : down  (south, facing camera)
 *
 * Verified visually 2026-04-28 — earlier comment had right/down swapped,
 * producing the "I walk right but character faces down" bug.
 */

export const FRAME_W = 32;
export const FRAME_H = 64;

export const CHAR_KEYS = ["adam", "alex", "bob", "amelia"] as const;
export type CharKey = typeof CHAR_KEYS[number];

/**
 * Map each team member ID to a character sprite. We have 4 free sprites
 * but 6 team members; the seniors share with their tier-pair (still
 * distinguishable by name label).
 */
export const MEMBER_TO_CHAR: Record<string, CharKey> = {
  kelvin:   "adam",      // manager
  kingston: "alex",      // manager
  ben:      "bob",       // 2nd mgr
  andy:     "bob",       // 2nd mgr
  donald:   "amelia",    // senior
  chris:    "amelia",    // senior
};

export function preloadCharacters(scene: Phaser.Scene) {
  for (const key of CHAR_KEYS) {
    scene.load.spritesheet(`${key}-run`, `/art/char-${key}-run.png`, {
      frameWidth: FRAME_W, frameHeight: FRAME_H,
    });
    scene.load.spritesheet(`${key}-idle`, `/art/char-${key}-idle.png`, {
      frameWidth: FRAME_W, frameHeight: FRAME_H,
    });
  }
}

export function createCharacterAnimations(scene: Phaser.Scene) {
  // Order MUST match the frame layout above: 0=right, 1=up, 2=left, 3=down.
  const dirs = ["right", "up", "left", "down"];
  for (const key of CHAR_KEYS) {
    for (let d = 0; d < 4; d++) {
      const dir = dirs[d];
      const start = d * 6;
      const end = start + 5;

      // run animation
      const runKey = `${key}-run-${dir}`;
      if (!scene.anims.exists(runKey)) {
        scene.anims.create({
          key: runKey,
          frames: scene.anims.generateFrameNumbers(`${key}-run`, { start, end }),
          frameRate: 12,
          repeat: -1,
        });
      }

      // idle animation
      const idleKey = `${key}-idle-${dir}`;
      if (!scene.anims.exists(idleKey)) {
        scene.anims.create({
          key: idleKey,
          frames: scene.anims.generateFrameNumbers(`${key}-idle`, { start, end }),
          frameRate: 6,
          repeat: -1,
        });
      }
    }
  }
}

export function makeSprite(scene: Phaser.Scene, x: number, y: number, charKey: CharKey): Phaser.GameObjects.Sprite {
  const s = scene.add.sprite(x, y, `${charKey}-idle`, 0);
  s.setOrigin(0.5, 0.75);    // anchor near feet, not centre
  s.play(`${charKey}-idle-down`);
  return s;
}

export function playMove(sprite: Phaser.GameObjects.Sprite, charKey: CharKey, direction: string, moving: boolean) {
  const baseKey = `${charKey}-${moving ? "run" : "idle"}-${direction}`;
  if (sprite.anims.currentAnim?.key !== baseKey) {
    sprite.play(baseKey);
  }
}
